import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { X, Loader2 } from 'lucide-react'
import { ModalEnum } from '@/interfaces'
import { useStores } from '@/stores/RootStoreContext'
import { getAvailablePrices } from '@/services/assistant-plan'
import Header from './header'
import PlanCard from './plan-card'

const MODAL_ID = ModalEnum.UPGRADE_PLAN

const buildPlans = (rawPlans, counterpartPlans, isMonthly) => {
  if (!rawPlans?.length) return []

  const recommendedIndex = rawPlans.length >= 3 ? 1 : 0

  return rawPlans.map((p, index) => {
    let priceHint = null
    if (isMonthly && counterpartPlans?.length) {
      const yearlyMatch = counterpartPlans.find((c) => c.product_id === p.product_id)
      if (yearlyMatch) {
        const monthlyTotal = p.unit_amount * 12
        if (monthlyTotal > yearlyMatch.unit_amount) {
          const pct = Math.round(((monthlyTotal - yearlyMatch.unit_amount) / monthlyTotal) * 100)
          if (pct > 0) priceHint = `Economize ${pct}% no anual`
        }
      }
    }

    return {
      id: p.id,
      plan_type: p.plan_type,
      product_id: p.product_id,
      name: p.product_name,
      price: p.unit_amount,
      period: p.interval === 'year' ? 'ano' : 'mês',
      features: Array.isArray(p.features) ? p.features : [],
      recommended: index === recommendedIndex,
      price_description: priceHint,
    }
  })
}

const computeYearlySavingsPct = (monthly, yearly) => {
  if (!monthly?.length || !yearly?.length) return null
  for (const m of monthly) {
    const y = yearly.find((yy) => yy.product_id === m.product_id)
    if (y && m.unit_amount * 12 > y.unit_amount) {
      return Math.round(((m.unit_amount * 12 - y.unit_amount) / (m.unit_amount * 12)) * 100)
    }
  }
  return null
}

const UpgradePlanModal = observer(() => {
  const { modalStore } = useStores()

  useEffect(() => {
    if (!modalStore.modals.has(MODAL_ID)) modalStore.addModal(MODAL_ID)
  }, [modalStore])

  const isOpen = modalStore.openModalsIds.includes(MODAL_ID)

  const [shouldRender, setShouldRender] = useState(false)
  const [show, setShow] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [prices, setPrices] = useState({ monthly: [], yearly: [] })
  const [billingPeriod, setBillingPeriod] = useState('monthly')

  const fetchPrices = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getAvailablePrices()
      const data = res?.data || {}
      const monthly = (data.mensal?.length && data.mensal) || data.monthly || []
      const yearly = (data.anual?.length && data.anual) || data.yearly || []
      setPrices({ monthly, yearly })
      setBillingPeriod(monthly.length === 0 && yearly.length > 0 ? 'yearly' : 'monthly')
    } catch {
      setError('Não foi possível carregar os planos. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) fetchPrices()
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
      const id = requestAnimationFrame(() => setShow(true))
      return () => cancelAnimationFrame(id)
    }
    setShow(false)
    const id = setTimeout(() => setShouldRender(false), 280)
    return () => clearTimeout(id)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return undefined
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = (e) => { if (e.key === 'Escape') modalStore.closeModal(MODAL_ID) }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen, modalStore])

  if (!shouldRender) return null

  const monthlyPlans = buildPlans(prices.monthly, prices.yearly, true)
  const yearlyPlans = buildPlans(prices.yearly, prices.monthly, false)
  const visiblePlans = billingPeriod === 'monthly' ? monthlyPlans : yearlyPlans
  const showToggle = monthlyPlans.length > 0 && yearlyPlans.length > 0
  const yearlySavingsPct = computeYearlySavingsPct(prices.monthly, prices.yearly)

  const gridCols =
    visiblePlans.length >= 3 ? 'sm:grid-cols-3' :
    visiblePlans.length === 2 ? 'sm:grid-cols-2 sm:max-w-[520px] sm:mx-auto' :
    'sm:max-w-[300px] sm:mx-auto'

  const closeModal = () => modalStore.closeModal(MODAL_ID)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-plan-title"
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-opacity duration-300 ease-out ${
        show ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div
        onClick={closeModal}
        aria-hidden="true"
        className="absolute inset-0 backdrop-blur-md"
        style={{ background: 'rgba(4,2,10,0.85)' }}
      />

      <div
        className={`relative z-10 flex w-full max-w-[820px] max-h-[calc(100vh-2rem)] flex-col overflow-hidden rounded-2xl transition-all duration-300 ease-out ${
          show ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-[0.98]'
        }`}
        style={{
          background: '#0a0612',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 32px 80px rgba(0,0,0,0.7)',
        }}
      >
        <button
          type="button"
          onClick={closeModal}
          aria-label="Fechar"
          className="absolute top-3 right-3 z-30 flex h-8 w-8 items-center justify-center rounded-full transition-transform hover:scale-105"
          style={{
            color: 'rgba(255,255,255,0.5)',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <X className="h-3.5 w-3.5" />
        </button>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 sm:p-7">
            <Header />

            {showToggle && !loading && !error && (
              <div className="mt-4 flex justify-center">
                <div
                  className="inline-flex items-center rounded-full p-0.5"
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  {[
                    { key: 'monthly', label: 'Mensal' },
                    { key: 'yearly', label: 'Anual' },
                  ].map((opt) => {
                    const active = billingPeriod === opt.key
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setBillingPeriod(opt.key)}
                        className="flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider transition-all active:scale-[0.98]"
                        style={{
                          color: active ? '#fff' : 'rgba(255,255,255,0.45)',
                          background: active
                            ? 'linear-gradient(135deg, #AD34FE 0%, #7b1fd4 100%)'
                            : 'transparent',
                          border: 'none',
                        }}
                      >
                        {opt.label}
                        {opt.key === 'yearly' && yearlySavingsPct && (
                          <span
                            className="rounded-full px-1.5 py-0.5 text-[9px] font-bold"
                            style={{
                              background: active ? 'rgba(255,255,255,0.2)' : 'rgba(173,52,254,0.12)',
                              color: active ? '#fff' : '#AD34FE',
                            }}
                          >
                            -{yearlySavingsPct}%
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {loading && (
              <div className="mt-6 flex flex-col items-center gap-2.5 py-8">
                <Loader2 className="h-5 w-5 animate-spin" style={{ color: '#AD34FE' }} />
                <p className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  Carregando planos
                </p>
              </div>
            )}

            {!loading && error && (
              <div className="mt-6 flex flex-col items-center gap-3 py-8">
                <p className="max-w-sm text-center text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  {error}
                </p>
                <button
                  type="button"
                  onClick={fetchPrices}
                  className="flex h-9 items-center gap-2 rounded-lg px-5 text-[13px] font-semibold transition-transform active:scale-[0.98]"
                  style={{
                    color: '#fff',
                    background: 'linear-gradient(135deg, #AD34FE 0%, #7b1fd4 100%)',
                    boxShadow: '0 0 24px rgba(173,52,254,0.35)',
                    border: 'none',
                  }}
                >
                  Tentar novamente
                </button>
              </div>
            )}

            {!loading && !error && visiblePlans.length > 0 && (
              <div className={`mt-5 grid grid-cols-1 gap-3 ${gridCols}`}>
                {visiblePlans.map((plan) => (
                  <PlanCard key={`${billingPeriod}-${plan.id}`} plan={plan} />
                ))}
              </div>
            )}

            {!loading && !error && visiblePlans.length === 0 && (
              <p className="mt-6 py-6 text-center text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Nenhum plano disponível no momento.
              </p>
            )}

            <p className="mt-5 text-center text-[11px]" style={{ color: 'rgba(255,255,255,0.25)' }}>
              Cancele quando quiser
              <span className="mx-2" style={{ color: 'rgba(255,255,255,0.12)' }}>·</span>
              Sem taxas ocultas
              <span className="mx-2" style={{ color: 'rgba(255,255,255,0.12)' }}>·</span>
              Suporte humano
            </p>
          </div>
        </div>
      </div>
    </div>
  )
})

export default UpgradePlanModal
